import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import {
  GbgrundbuchgeschaeftService,
  GrundbuchgeschaeftListEntryMeta,
  GrundstueckId,
  LoadGbGeschaeftSichtByGrundstueckIdsRequest
} from '../../../core/gb-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { SortDirection, TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { ListLoaderPaginationBuilder } from '../../../shared/components/list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../shared/components/list-loader/pagination-lookup-result';
import { TranslatePipe } from '@ngx-translate/core';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { KompaktGrundbuchgeschaeftComponent } from '../../../shared/kompaktdarstellung/grundbuchgeschaeft/kompakt-grundbuchgeschaeft.component';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { PermissionResponse } from '../../../core/base-api';
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import SortEnum = LoadGbGeschaeftSichtByGrundstueckIdsRequest.SortEnum;

@Component({
  selector: 'cap-haengige-gb-geschaefte-icp',
  imports: [
    TranslatePipe,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    KompaktGrundbuchgeschaeftComponent,
    LinkComponent,
    DatumPipe
  ],
  templateUrl: './haengige-gb-geschaefte-icp.component.html'
})
export class HaengigeGbGeschaefteIcpComponent extends AbstractComponent implements OnDestroy, OnChanges {
  @Input()
  perimeter: Array<GrundstueckId>;
  @Input()
  hasRight: boolean = true;

  haengigeLoader: ListLoader<GrundbuchgeschaeftListEntryMeta>;
  tableSortModel = new TableSortModel<GrundbuchgeschaeftListEntryMeta>('table', this.comparatorService);
  hasPermissionAuskunft: boolean;
  private readonly PAGE_SIZE = 5;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private grundbuchgeschaeftService: GbgrundbuchgeschaeftService,
    private comparatorService: ComparatorService,
    private appPermissionService: AppPermissionService
  ) {
    super();
    this.tableSortModel.addColumn('tagebuchnummer', undefined, SortEnum.TAGEBUCHNUMMER).changeSortMode(SortDirection.DESC);
    this.tableSortModel.addColumn('status', undefined, SortEnum.STATUS);
    this.tableSortModel.addColumn('eingangTagebuch', undefined, SortEnum.EINGANG_TAGEBUCH);
    this.hasPermissionAuskunft = this.appPermissionService.hasPermission(PermissionsEnum.AUSKUNFTSPERRE);
  }

  private _loadOption;

  @Input()
  set loadOption(value: string) {
    if (!value) {
      return;
    }
    this._loadOption = value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.perimeter?.currentValue || this.perimeter) {
      this.updateLoader();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
  }

  private updateLoader(): void {
    if (!this.hasRight || !this.perimeter) {
      return;
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
    if (this.perimeter.length === 1) {
      this.haengigeLoader = this.createGbGeschaeftByGrundstueckIdLoader();
    } else {
      this.haengigeLoader = this.createGbGeschaeftByGrundstueckIdsLoader();
    }
    this.haengigeLoader.applySortModel(this.tableSortModel);
  }

  private createGbGeschaeftByGrundstueckIdLoader(): ListLoader<GrundbuchgeschaeftListEntryMeta> {
    const loader = ListLoaderPaginationBuilder.builder<GrundbuchgeschaeftListEntryMeta>()
      .setLookupItemFunc((offset, limit, sortColumn) => this.createGbGeschaeftByGrundsuteckIdObservable(offset, limit, sortColumn))
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGbGeschaeftByGrundsuteckIdObservable(offset, limit, sortColumn) {
    return this.grundbuchgeschaeftService
      .loadGbGeschaeftSichtGrundstueck(
        this.perimeter[0].id,
        offset,
        limit,
        sortColumn.sortDirectionBackend,
        sortColumn.sortEnumBackend,
        this._loadOption
      )
      .pipe(
        map((r) => {
          return <PaginationLookupResult<GrundbuchgeschaeftListEntryMeta>>{
            result: r.results,
            totalItems: r.totalItems
          };
        })
      );
  }

  private createGbGeschaeftByGrundstueckIdsLoader(): ListLoader<GrundbuchgeschaeftListEntryMeta> {
    const loader = ListLoaderPaginationBuilder.builder<GrundbuchgeschaeftListEntryMeta>()
      .setLookupItemFunc((offset, limit, sortColumn) => this.createGbGeschaeftByGrundsuteckIdsObservable(offset, limit, sortColumn))
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGbGeschaeftByGrundsuteckIdsObservable(offset, limit, sortColumn) {
    const request: LoadGbGeschaeftSichtByGrundstueckIdsRequest = {
      grundstueckIds: this.perimeter,
      auftragLoadOption: 'HAENGIGE',
      offset: offset,
      limit: limit,
      sortDirection: sortColumn.sortDirectionBackend,
      sort: sortColumn.sortEnumBackend
    };
    return this.grundbuchgeschaeftService.loadGbGeschaeftSichtGrundstuecke(request).pipe(
      map((r) => {
        return <PaginationLookupResult<GrundbuchgeschaeftListEntryMeta>>{
          result: r.results,
          totalItems: r.totalItems
        };
      })
    );
  }
}
